import { Component, inject } from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { combineLatest, distinctUntilChanged, filter, map } from "rxjs";

import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";
import { DialogWrapperComponent } from "@bitwarden/web-vault/app/components/wrappers/dialog-wrapper/dialog-wrapper.component";
import { ManuallyAddInstitutionComponent } from "@bitwarden/web-vault/app/components/manually-add-institution/manually-add-institution.component";
import { TablesFactory } from "@bitwarden/web-vault/app/components/tables/tables.factory";
import { TablesType } from "@bitwarden/web-vault/app/models/enum/tables.enum";
import { InstitutionDetailsTable } from "@bitwarden/web-vault/app/components/tables/institution-tables/institution-details-table/institution-details.table";
import { manageButtonProperties } from "../manage.components";

@Component({
  selector: "app-manage-institutions",
  templateUrl: "./manage-institutions.component.html",
})
export class ManageInstitutionsComponent {
  private readonly i18nService = inject(I18nService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);

  table: InstitutionDetailsTable = new TablesFactory().get(TablesType.InstitutionDetails, this);

  private readonly institutions$ = toObservable(this.table.data);

  private readonly institutionId$ = this.route.queryParams.pipe(
    map((params) => params["institutionId"] as string | undefined),
    distinctUntilChanged(),
  );

  addInstitutionButton = new ActionButton({
    ...manageButtonProperties,
    text: this.i18nService.t("addInstitution"),
    onClick: () => this.openAddInstitutionDialog(),
  });

  constructor() {
    combineLatest([this.institutionId$, this.institutions$])
      .pipe(
        filter(
          ([institutionId, institutions]) =>
            !!institutionId && Array.isArray(institutions) && institutions.length > 0,
        ),
        takeUntilDestroyed(),
      )
      .subscribe(([institutionId, institutions]) => {
        const matchingInstitution = institutions.find((inst) => inst.id === institutionId);
        if (!matchingInstitution) {
          return;
        }
        if (this.table.institutionId() !== matchingInstitution.id) {
          this.table.onRowClicked(matchingInstitution.id);
        }
        this.clearInstitutionIdFromUrl();
      });
  }

  /** Drop deep-link query param after selection; UI state lives on the table signal. */
  private clearInstitutionIdFromUrl(): void {
    if (!this.route.snapshot.queryParamMap.get("institutionId")) {
      return;
    }
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { institutionId: null },
      replaceUrl: true,
    });
  }

  protected openAddInstitutionDialog(): void {
    this.dialog.open(DialogWrapperComponent, {
      width: "400px",
      data: {
        title: "manuallyAddInstitution",
        showBackButton: false,
        component: ManuallyAddInstitutionComponent,
        componentInputs: {
          selectedCountry: null,
        },
      },
      disableClose: true,
    });
  }
}
